import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, Cigarette } from 'lucide-react';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { abitudiniAPI } from '../../services/api';
import '../../styles/SchedaFisiologica.css';

/**
 * RiquadroAbitudini — fumo e alcol (proposta 16, decisioni di Alex del 27
 * settembre 2026).
 *
 * UN DATO SOLO, SCRITTO DA UN POSTO SOLO: l'anamnesi. Ogni aggiornamento e'
 * una riga nuova dello storico (migration 255), con momento e autore messi
 * dal database; il riquadro mostra l'ultima e, a richiesta, le precedenti.
 * Il server compone la versione nuova sopra l'ultima, un campo per volta:
 * qui non si manda mai la fotografia intera, che potrebbe essere vecchia.
 *
 * NELLE SCHEDE DI BRANCA e' in SOLA LETTURA (decisione 1a): il valore al
 * giorno della compilazione, con «per modificarlo apri l'anamnesi». Tre
 * schede del catalogo avevano campi di fumo e alcol, vuoti, ritirati dalla
 * 255: il riquadro ne prende il posto, in testa alla scheda. Di fatto oggi
 * si vede nella cardiologica: la pelle e la gastroenterologica del catalogo
 * hanno un questionario, e il profilo le compila da li' (con le sue
 * domande), non da queste schermate. Restano nell'elenco perche' se un
 * giorno si compilano da qui il riquadro deve esserci.
 *
 * SOLO IL NUMERO (Alex, 28/09/2026, migration 264): la quantita' in testo
 * libero non si scrive piu'; resta un numero a destra delle scelte, con
 * l'unita' accanto presa da `parametri` (arriva con la lettura). I testi
 * scritti prima restano nello storico: si vedono fra i precedenti, e finche'
 * l'ultima versione ne ha uno, sotto la riga come «scritto prima».
 */

export const SCHEDE_CON_ABITUDINI = ['scheda_pelle', 'scheda_gastroenterologica', 'scheda_cardiologia'];

const FUMO = [
    { valore: 'mai', etichetta: 'Mai' },
    { valore: 'ex', etichetta: 'Ex' },
    { valore: 'si', etichetta: 'Sì' },
];
const SI_NO = [
    { valore: true, etichetta: 'Sì' },
    { valore: false, etichetta: 'No' },
];
const CONFERMA_FUMO = 'Quello scritto accanto si toglie. Procedo? Resta nei precedenti.';
const CONFERMA_ALCOL = 'Il numero dei bicchieri si toglie. Procedo? Resta nei precedenti.';
/* Le etichette se la lettura non le porta: le stesse parole di parametri. */
const UNITA = { fumo: 'sigarette al giorno', alcol: 'bicchieri al giorno' };
/* I numeri: limite e messaggio, gli stessi del server (services/abitudini.js). */
const NUMERI = {
    fumo_sigarette_giorno: { max: 100, msg: 'Le sigarette al giorno sono un numero intero da 0 a 100: è un limite per fermare un errore di battitura.' },
    alcol_bicchieri_giorno: { max: 30, msg: 'I bicchieri al giorno sono un numero intero da 0 a 30: è un limite per fermare un errore di battitura.' },
};
const pieno = (x) => x !== null && x !== undefined && String(x) !== '';

/** «27/09/2026 12:40», ora italiana. */
const quando = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d)) return '';
    return d.toLocaleString('it-IT', {
        timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

/** «2026-03-15» -> «15/03/2026». */
const dataItaliana = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/** Una versione in una riga: la sola lettura e i precedenti la dicono cosi'.
    Le versioni scritte prima del 28/09/2026 possono avere il testo della
    quantita': si mostra, e' la loro storia. */
export function riassunto(v, unita = UNITA) {
    if (!v) return '';
    const parti = [];
    if (v.fumo === 'mai') parti.push('Fumo: mai');
    if (v.fumo === 'ex') parti.push(`Fumo: ex${v.fumo_smesso ? ` (ha smesso: ${v.fumo_smesso})` : ''}`);
    if (v.fumo === 'si') {
        const dett = [pieno(v.fumo_sigarette_giorno) ? `${v.fumo_sigarette_giorno} ${unita.fumo}` : null,
            v.fumo_quantita].filter(Boolean).join(', ');
        parti.push(`Fumo: sì${dett ? ` (${dett})` : ''}`);
    }
    if (v.alcol === true) {
        const dett = [pieno(v.alcol_bicchieri_giorno) ? `${v.alcol_bicchieri_giorno} ${unita.alcol}` : null,
            v.alcol_quantita].filter(Boolean).join(', ');
        parti.push(`Alcol: sì${dett ? ` (${dett})` : ''}`);
    }
    if (v.alcol === false) parti.push('Alcol: no');
    return parti.join(' · ');
}

const vuoto = () => ({
    fumo: null, fumo_sigarette_giorno: '', fumo_smesso: '', alcol: null, alcol_bicchieri_giorno: '',
});
const dalServer = (u) => ({
    fumo: u?.fumo ?? null,
    fumo_sigarette_giorno: u?.fumo_sigarette_giorno ?? '',
    fumo_smesso: u?.fumo_smesso ?? '',
    alcol: u?.alcol ?? null,
    alcol_bicchieri_giorno: u?.alcol_bicchieri_giorno ?? '',
});

/**
 * @param {number}   pazienteId
 * @param {number}   compilazioneId   dentro una scheda: la compilazione aperta (sola lettura)
 * @param {number}   visitaId         dentro una procedura: la visita, se la scheda non e' salvata
 * @param {boolean}  valoreDiOggi     sopra i questionari pelle e gastroenterologia: il valore di
 *                                    adesso, in sola lettura (Alex, 27/09/2026). Un questionario
 *                                    ha le sue date, e l'elenco non ne ha una sola
 * @param {function} onApriAnamnesi   il collegamento «apri l'anamnesi», dove c'e'
 */
const RiquadroAbitudini = ({ pazienteId, compilazioneId = null, visitaId = null, valoreDiOggi = false,
                             onApriAnamnesi = null }) => {
    const alGiorno = !!(compilazioneId || visitaId);
    const inScheda = alGiorno || valoreDiOggi;
    const [dati, setDati] = useState(null);
    const [ultimo, setUltimo] = useState(null);
    const [precedenti, setPrecedenti] = useState([]);
    const [apriPrecedenti, setApriPrecedenti] = useState(false);
    const [giorno, setGiorno] = useState(null);
    const [numeroSbagliato, setNumeroSbagliato] = useState(null);   // il campo, o null
    const [unita, setUnita] = useState(UNITA);
    const [versione, setVersione] = useState(0);
    const [errore, setErrore] = useState(null);
    const inSchedaOra = useRef(inScheda);
    inSchedaOra.current = inScheda;
    /* L'ultima versione anche fuori dallo stato: chi salva la sposta fra i
       precedenti, e un aggiornamento di stato dentro un altro partirebbe
       due volte. */
    const ultimoOra = useRef(null);

    const carica = useCallback(async () => {
        try {
            if (alGiorno) {
                const r = await abitudiniAPI.alGiorno(pazienteId,
                    compilazioneId ? { compilazione: compilazioneId } : { visita: visitaId });
                const x = r.data?.data || {};
                setGiorno(x.giorno || null);
                if (x.unita) setUnita(x.unita);
                setUltimo(x.valore || null);
                ultimoOra.current = x.valore || null;
                setDati(dalServer(x.valore));
            } else {
                const r = await abitudiniAPI.leggi(pazienteId);
                const x = r.data?.data || {};
                if (x.unita) setUnita(x.unita);
                setUltimo(x.ultimo || null);
                ultimoOra.current = x.ultimo || null;
                setPrecedenti(x.precedenti || []);
                setDati(x.ultimo ? dalServer(x.ultimo) : vuoto());
            }
            setVersione((v) => v + 1);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a leggere le abitudini');
        }
    }, [pazienteId, alGiorno, compilazioneId, visitaId]);

    useEffect(() => { carica(); }, [carica]);

    /* Un campo per volta: la risposta porta l'ultima versione, e se e'
       nata una riga nuova quella di prima passa fra i precedenti. */
    const scrivi = useCallback(async (campo, valore) => {
        const r = await abitudiniAPI.scrivi(pazienteId, campo, valore);
        const esito = r.data?.data;
        if (esito?.cambiato) {
            const prima = ultimoOra.current;
            ultimoOra.current = esito.ultimo;
            if (prima) setPrecedenti((p) => [prima, ...p]);
            setUltimo(esito.ultimo);
        }
        return r;
    }, [pazienteId]);
    const { stato: statoCampi, salva: salvaVero, nonSalvati } = useSalvaCampo(
        scrivi, useCallback(() => setVersione((v) => v + 1), []));
    /* In una scheda non parte niente: e' sola lettura, anche se un campo
       perdesse il fuoco. */
    const salvaCampo = useCallback((campo, valore) => {
        if (inSchedaOra.current) return;
        salvaVero(campo, valore);
    }, [salvaVero]);

    const sporco = useSporco(dati || {}, versione);
    useUscitaProtetta(!inScheda && (sporco || nonSalvati.length > 0), nonSalvati.length || null);

    const cambia = (campo, valore) => setDati((p) => ({ ...(p || vuoto()), [campo]: valore }));

    const segno = (campo) => {
        const st = statoCampi[campo];
        if (!st) return null;
        if (st === 'in_corso') return <span className="sf-segno sf-segno--corso" title="Sto salvando…">…</span>;
        if (st === 'salvato') return <span className="sf-segno sf-segno--ok" title="Salvato"><Check size={13} aria-hidden="true" /></span>;
        return (
            <button type="button" className="sf-segno sf-segno--no"
                    title="Non salvato: clicca per riprovare"
                    onClick={() => salvaCampo(campo, dati?.[campo] ?? null)}>non salvato</button>
        );
    };
    const classeCampo = (campo) => (statoCampi[campo] === 'non_salvato' ? 'sf-testo--rosso' : '');

    /* Ripremere la scelta gia' fatta la toglie. Cambiando la risposta, i
       campi che ne dipendono si svuotano - lo fa anche il server - e se
       c'era scritto qualcosa si chiede prima. */
    const scegliFumo = (valore) => {
        const nuovo = dati?.fumo === valore ? null : valore;
        const perde = (nuovo !== 'si' && pieno(dati?.fumo_sigarette_giorno))
            || (nuovo !== 'ex' && String(dati?.fumo_smesso || ''));
        if (perde && !window.confirm(CONFERMA_FUMO)) return;
        setDati((p) => ({
            ...(p || vuoto()), fumo: nuovo,
            ...(nuovo !== 'si' ? { fumo_sigarette_giorno: '' } : {}),
            ...(nuovo !== 'ex' ? { fumo_smesso: '' } : {}),
        }));
        if (numeroSbagliato === 'fumo_sigarette_giorno') setNumeroSbagliato(null);
        salvaCampo('fumo', nuovo);
    };
    const scegliAlcol = (valore) => {
        const nuovo = dati?.alcol === valore ? null : valore;
        if (nuovo !== true && pieno(dati?.alcol_bicchieri_giorno) && !window.confirm(CONFERMA_ALCOL)) return;
        setDati((p) => ({ ...(p || vuoto()), alcol: nuovo, ...(nuovo !== true ? { alcol_bicchieri_giorno: '' } : {}) }));
        if (numeroSbagliato === 'alcol_bicchieri_giorno') setNumeroSbagliato(null);
        salvaCampo('alcol', nuovo);
    };

    /* I numeri sono facoltativi (decisione 2b): un limite per fermare un
       errore di battitura. Fuori da li' non parte. */
    const salvaNumero = (campo, testo) => {
        const t = String(testo ?? '').trim();
        if (!t) { setNumeroSbagliato(null); salvaCampo(campo, null); return; }
        const n = Number(t);
        if (!Number.isInteger(n) || n < 0 || n > NUMERI[campo].max) { setNumeroSbagliato(campo); return; }
        setNumeroSbagliato(null);
        salvaCampo(campo, n);
    };

    if (!dati) return errore ? <p className="sf-errore-riga">{errore}</p> : null;

    if (inScheda) {
        return (
            <section className="sf-riquadro sf-riquadro--in-scheda sf-riquadro--sola-lettura">
                <h3><Cigarette size={16} strokeWidth={1.8} aria-hidden="true" /> Abitudini</h3>
                <span className="sf-valore-al">
                    {alGiorno ? `Valore al ${dataItaliana(giorno)}` : 'Valore di oggi'} — per modificarlo{' '}
                    {onApriAnamnesi
                        ? <button type="button" className="sf-link" onClick={onApriAnamnesi}>apri l&apos;anamnesi</button>
                        : 'apri l\'anamnesi'}
                </span>
                <p className="sf-abitudini-testo">
                    {ultimo ? riassunto(ultimo, unita)
                        : alGiorno ? 'A quella data non era registrato niente.' : 'Nell\'anamnesi non è ancora registrato niente.'}
                </p>
            </section>
        );
    }

    const rigaScelta = (campo, titolo, opzioni, scegli) => (
        <div>
            <span className="sf-etichetta">{titolo}</span>
            <div className="sf-spunte sf-spunte-riga">
                {opzioni.map((o) => (
                    <label key={String(o.valore)} className="sf-spunta">
                        <input type="checkbox" checked={dati[campo] === o.valore} onChange={() => scegli(o.valore)} />
                        <span>{o.etichetta}</span>
                    </label>
                ))}
                {segno(campo)}
            </div>
        </div>
    );

    /* Il numero a destra delle scelte, sulla stessa riga, con l'unita'
       scritta accanto (Alex, 28/09/2026). */
    const numero = (campo, etichettaUnita) => (
        <div className="sf-numero-unita">
            <input type="number" min="0" max={NUMERI[campo].max} step="1" inputMode="numeric"
                aria-label={etichettaUnita}
                className={`sf-testo sf-data sf-numero ${numeroSbagliato === campo ? 'errore' : classeCampo(campo)}`}
                value={dati[campo] ?? ''}
                onChange={(e) => cambia(campo, e.target.value)}
                onBlur={(e) => salvaNumero(campo, e.target.value)} />
            <span className="sf-unita">{etichettaUnita}</span>
            {segno(campo)}
        </div>
    );
    /* Il testo scritto prima del 28/09/2026 sull'ultima versione: non si
       modifica piu', ma non sparisce finche' non nasce una versione nuova. */
    const scrittoPrima = (testoVecchio) => (testoVecchio
        ? <span className="sf-aiuto sf-scritto-prima">Scritto prima: «{testoVecchio}»</span> : null);

    const testo = (campo, etichetta, segnaposto) => (
        <div>
            <span className="sf-etichetta">{etichetta}</span>
            <input className={`sf-testo sf-testo--pieno ${classeCampo(campo)}`}
                placeholder={segnaposto}
                maxLength={300}
                value={dati[campo] || ''}
                onChange={(e) => cambia(campo, e.target.value)}
                onBlur={(e) => salvaCampo(campo, e.target.value || null)} />
            {segno(campo)}
        </div>
    );

    return (
        <section data-salva-da-solo className="sf-riquadro">
            <h3><Cigarette size={16} strokeWidth={1.8} aria-hidden="true" /> Abitudini</h3>
            {ultimo && (
                <span className="sf-ultima-modifica">
                    Ultimo aggiornamento: <b>{ultimo.autore_nome || 'utente non indicato'}</b>, {quando(ultimo.valido_dal)}
                </span>
            )}
            <span className="sf-aiuto">
                Si salva appena esci dal campo. Ogni aggiornamento resta fra i precedenti; le schede di branca
                lo mostrano in sola lettura.
            </span>

            <div className="sf-riga-scelta sf-riga-abitudini">
                {rigaScelta('fumo', 'Fumo', FUMO, scegliFumo)}
                {dati.fumo === 'si' && numero('fumo_sigarette_giorno', unita.fumo)}
                {dati.fumo === 'ex' && testo('fumo_smesso', 'Quando ha smesso', 'Per esempio: 2019, da tre anni')}
            </div>
            {dati.fumo === 'si' && scrittoPrima(ultimo?.fumo_quantita)}

            <div className="sf-riga-scelta sf-riga-abitudini">
                {rigaScelta('alcol', 'Alcol', SI_NO, scegliAlcol)}
                {dati.alcol === true && numero('alcol_bicchieri_giorno', unita.alcol)}
            </div>
            {dati.alcol === true && scrittoPrima(ultimo?.alcol_quantita)}
            {numeroSbagliato && <span className="sf-errore-riga">{NUMERI[numeroSbagliato].msg}</span>}

            {precedenti.length > 0 && (
                <div className="sf-precedenti">
                    <button type="button" className="sf-link" onClick={() => setApriPrecedenti((x) => !x)}>
                        {apriPrecedenti ? 'Nascondi i precedenti' : `Precedenti (${precedenti.length})`}
                    </button>
                    {apriPrecedenti && (
                        <ul>
                            {precedenti.map((p) => (
                                <li key={p.id}>
                                    <span className="sf-aiuto">{quando(p.valido_dal)}, {p.autore_nome || 'utente non indicato'}:</span>
                                    {' '}{riassunto(p, unita) || 'nessuna risposta'}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
};

export default RiquadroAbitudini;
